import { useLayoutEffect, useRef, useState } from "react";
import { Input } from "antd";
import {
  CheckOutlined,
  CommentOutlined,
  EditOutlined,
  MinusOutlined,
  PlusOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  useActiveCategories,
  useEnabledItems,
  useMenuAddons,
  useMenuModifiers,
  useMenuStations,
} from "../../../../hooks/useMenu";
import {
  buildGroupColorLookup,
  stationColorOf,
  type ColorBy,
} from "./catalogColors";
import { getItemName, getTranslation } from "../../../../utils/translations";
import { LunchTag } from "../../lunch/LunchTag";
import { KbdChip } from "../modalPrimitives";
import {
  buildOrderRows,
  calculateOrderTotal,
  calculateOrderPricing,
} from "./orderSummaryUtils";
import { formatSignedBillPercent } from "../billDiscount";

const { TextArea } = Input;

interface OrderItem {
  itemId: number;
  quantity: number;
  modifiers: number[];
  addons: Record<number, Record<number, number>>; // addonId -> { itemId -> количество }
  comment?: string;
}

interface OrderPanelContentProps {
  orderItems: OrderItem[];
  comment: string;
  showCommentInput: boolean;
  onCommentChange: (comment: string) => void;
  onToggleComment: () => void;
  onQuantityChange: (index: number, delta: number) => void;
  /** Отваря модала за редакция на модификатори/коментар от молива на реда
   *  (EditModifiersModal, рендира се в Ordering); при обобщен ред получава
   *  всички индекси на групата. */
  onItemEdit: (indices: number[], orderItem: OrderItem) => void;
  onSubmit: () => void;
  /** true, докато createBill заявката тече — блокира „Поръчай" */
  submitting: boolean;
  /** "panel" (десктоп панел) или "sheet" (мобилен bottom sheet) — сменя само фона на редовете и празния текст */
  variant?: "panel" | "sheet";
  /** Клиент с отстъпка/надбавка по сметката (знаков процент, >0 = отстъпка) —
   *  блок над „Общо" с основата и корекцията; null = без корекция. */
  billDiscount?: { percent: number; clientName: string | null } | null;
  /** По какво се цвети лентата отляво на реда (настройка на ресторанта colorBy):
   *  станция → една ивица на станция (продукт с 2 станции = 2 половини);
   *  група → цветът на групата. */
  colorBy?: ColorBy;
}

/** Показва количество без излишни нули, до 3 знака — колкото приема модалът
 *  „Промени" (3 → "3", 0.5 → "0.5", 0.543 → "0.543"). Закръгляне до 1 знак
 *  тук показваше 0.543 като "0.5" — лъжеше за реалното количество. */
const formatQuantity = (quantity: number): string =>
  String(parseFloat(quantity.toFixed(3)));

/**
 * Споделено тяло на „Преглед на поръчката" — ползва се и от десктоп панела
 * (OrderPanel), и от мобилния bottom sheet (OrderSheet). Съдържа редовете на
 * текущата поръчка със степери, коментара към поръчката и бутоните
 * „Коментар" / „Поръчай · {сума}".
 */
export const OrderPanelContent = ({
  orderItems,
  comment,
  showCommentInput,
  onCommentChange,
  onToggleComment,
  onQuantityChange,
  onItemEdit,
  onSubmit,
  submitting,
  variant = "panel",
  billDiscount = null,
  colorBy = "group",
}: OrderPanelContentProps) => {
  const { t, i18n } = useTranslation();
  const allItems = useEnabledItems();
  const allAddons = useMenuAddons();
  const allModifiers = useMenuModifiers();
  const stations = useMenuStations();
  const categories = useActiveCategories();
  const groupColor = buildGroupColorLookup(categories);
  // Цветовете на лентата отляво на реда — по станции или по група
  const stripeColors = (item: {
    stationIds: number[];
    categoryId: number | null;
  }) => {
    if (colorBy === "none") return [];
    if (colorBy === "station") {
      return item.stationIds
        .map((id) => stationColorOf(stations.find((s) => s.id === id)))
        .filter((color): color is string => !!color);
    }
    const color = groupColor(item.categoryId);
    return color ? [color] : [];
  };

  // „Обобщено" — по подразбиране изключено; слива еднаквите редове в един със
  // сборно количество. Живее тук, за да важи и за панела, и за sheet-а.
  const [summarized, setSummarized] = useState(false);

  const total = calculateOrderTotal(orderItems, allItems, allAddons);
  // Клиентски процент по сметката: „Общо" и „Поръчай" показват коригираната
  // сума (BE ще я приложи при поръчката), блокът отгоре — основата и разликата
  const billPercent = billDiscount?.percent ?? 0;
  const pricing = calculateOrderPricing(
    orderItems,
    allItems,
    allAddons,
    billPercent,
  );
  const payableTotal = billPercent !== 0 ? pricing.finalTotal : total;
  const isEmpty = orderItems.length === 0;
  const isSheet = variant === "sheet";
  const rows = buildOrderRows(orderItems, summarized);

  // При всяко МАРКИРАНЕ редът с последно добавеното се докарва в полезрението:
  // при дълга поръчка сервитьорът вижда какво току-що е маркирал, не началото
  // на списъка. Маркираното винаги стои в ПОСЛЕДНИЯ запис на кошницата
  // (Ordering.tsx добавя нов запис или слива само с последния), затова
  // сигналът е точно това: кошницата се е удължила или последният запис е
  // пораснал. Степерът „+" на ред по средата, редакция от молива, намаляване
  // и махане НЕ скролират — ръката е на реда, който се пипа. В обобщен
  // изглед целевият ред може да е и по средата — затова `nearest`, не дъното.
  const listRef = useRef<HTMLDivElement>(null);
  const recordCount = orderItems.length;
  const lastQuantity = orderItems[recordCount - 1]?.quantity ?? 0;
  const previousRef = useRef({ recordCount: 0, lastQuantity: 0 });
  useLayoutEffect(() => {
    const previous = previousRef.current;
    previousRef.current = { recordCount, lastQuantity };
    const marked =
      recordCount > previous.recordCount ||
      (recordCount === previous.recordCount &&
        lastQuantity > previous.lastQuantity);
    if (!marked) return;
    listRef.current
      ?.querySelector("[data-order-last]")
      ?.scrollIntoView({ block: "nearest" });
  }, [recordCount, lastQuantity]);

  // Малък степер за редовете в прегледа (32×32, рамкирана група). При обобщен
  // ред промяната отива в ПОСЛЕДНИЯ от слетите записи — така „−" маха цял
  // запис, когато той стигне 0, а сборът на реда пада точно с една бройка.
  const stepper = (indices: number[], quantity: number) => {
    const targetIndex = indices[indices.length - 1];
    return (
      <span className="inline-flex items-stretch border border-pos-ctl rounded-[9px] overflow-hidden bg-pos-card shrink-0">
        <button
          type="button"
          onClick={() => onQuantityChange(targetIndex, -1)}
          className="w-8 h-8 bg-pos-stepper text-pos-dim text-[17px] leading-none grid place-items-center cursor-pointer"
        >
          <MinusOutlined className="text-[14px]" />
        </button>
        <span
          className={`min-w-[26px] px-1 grid place-items-center font-pos-mono font-semibold text-pos-ink ${isSheet ? "text-sm" : "text-base"}`}
        >
          {formatQuantity(quantity)}
        </span>
        <button
          type="button"
          onClick={() => onQuantityChange(targetIndex, 1)}
          className="w-8 h-8 bg-pos-stepper text-pos-accent-ink text-[17px] leading-none grid place-items-center cursor-pointer"
        >
          <PlusOutlined className="text-[14px]" />
        </button>
      </span>
    );
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Списък с редовете на поръчката */}
      {/* Скролбарът е скрит (както в каталога) — без вертикална линия */}
      <div
        ref={listRef}
        className="flex-1 min-h-0 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden p-3 flex flex-col gap-1"
      >
        {isEmpty ? (
          <div className="flex-1 grid place-items-center px-2.5 py-10 text-center text-sm text-pos-muted">
            {isSheet ? t("ordering.emptyOrderShort") : t("ordering.emptyOrder")}
          </div>
        ) : (
          rows.map((row, index) => {
            const orderItem = row.orderItem;
            const item = allItems.find((i) => i.id === orderItem.itemId);
            if (!item) return null;

            const itemName = getItemName(
              item.itemLangs,
              i18n.language,
              item.slug,
            );
            const quantity = row.quantity;
            const price = item.price;

            // Добавки: totalQty е ФИНАЛНИЯТ брой — тип "1" (обикновена) е с
            // абсолютно количество от брояча в модала (не зависи от бройката на
            // продукта) → × броя слети записи; тип "2"/"3" са по 1 на продукт →
            // × сборното quantity.
            const addons: { name: string; price: number; totalQty: number }[] =
              [];
            Object.entries(orderItem.addons).forEach(([addonId, qtyMap]) => {
              const addon = allAddons.find((a) => a.id === Number(addonId));
              if (addon) {
                const absolute = addon.type === 1;
                Object.entries(qtyMap).forEach(([addonItemId, addonQty]) => {
                  const itemId = Number(addonItemId);
                  const addonItemData = addon.items.find(
                    (ai) => ai.itemId === itemId,
                  );
                  const addonItemPrice =
                    addonItemData?.addonItemPrice ?? addonItemData?.price ?? 0;
                  const addonItem = allItems.find((i) => i.id === itemId);
                  const addonName = addonItem
                    ? getItemName(
                        addonItem.itemLangs,
                        i18n.language,
                        addonItem.slug,
                      )
                    : `#${itemId}`;
                  addons.push({
                    name: addonName,
                    price: addonItemPrice,
                    totalQty: absolute
                      ? addonQty * row.indices.length
                      : addonQty * quantity,
                  });
                });
              }
            });

            const addonsLineTotal = addons.reduce(
              (sum, a) => sum + a.price * a.totalQty,
              0,
            );
            const totalItemPrice = price * quantity + addonsLineTotal;

            // Модификатори (по име) — показват се като chips до коментара
            const modifiers: string[] = orderItem.modifiers
              .map((modId) => {
                const modifier = allModifiers.find((m) => m.id === modId);
                return modifier
                  ? getTranslation(
                      modifier.translations,
                      i18n.language,
                      modifier.slug,
                    )
                  : "";
              })
              .filter(Boolean);

            const stripes = stripeColors(item);

            return (
              <div
                key={index}
                data-order-last={
                  row.indices.includes(orderItems.length - 1) ? "" : undefined
                }
                // shrink-0: overflow-hidden сваля min-height на flex item-а до 0
                // и редовете се свиваха вместо списъкът да скролира
                className={`${
                  isSheet
                    ? "bg-pos-panel rounded-[11px]"
                    : "bg-pos-card rounded-[10px]"
                } border border-pos-line3 flex shrink-0 overflow-hidden`}
              >
                {/* Лента отляво в цвета на станцията/групата — при няколко
                    станции всяка е равна част (цветовете са от базата →
                    inline style) */}
                {stripes.length > 0 && (
                  <span
                    aria-hidden
                    className="w-1 shrink-0 self-stretch flex flex-col"
                  >
                    {stripes.map((color, stripeIndex) => (
                      <span
                        key={stripeIndex}
                        className="flex-1"
                        style={{ background: color }}
                      />
                    ))}
                  </span>
                )}
                <div
                  className={`flex-1 min-w-0 flex flex-col gap-1.5 ${
                    isSheet ? "px-3 py-2.5" : "px-[11px] py-[9px]"
                  }`}
                >
                  {/* Ред 1: молив (коментар към продукта) + име + сума на реда */}
                  <div className="flex items-center gap-1.5 min-w-0">
                    <button
                      type="button"
                      title={t("ordering.itemComment")}
                      onClick={() => onItemEdit(row.indices, orderItem)}
                      className={`shrink-0 border-0 bg-transparent p-0 grid place-items-center ${isSheet ? "text-[13px]" : "text-[15px]"} cursor-pointer ${
                        orderItem.comment || modifiers.length > 0
                          ? "text-pos-accent"
                          : "text-pos-faint"
                      }`}
                    >
                      <EditOutlined />
                    </button>
                    <span
                      title={itemName}
                      className={`flex-1 min-w-0 font-semibold text-pos-ink truncate ${isSheet ? "text-[13px]" : "text-[15px] leading-[19.5px]"}`}
                    >
                      {itemName}
                    </span>
                    {item.lunchMenuId != null && <LunchTag />}
                    <span
                      className={`font-pos-mono font-bold text-pos-ink shrink-0 ${isSheet ? "text-[13px]" : "text-[15px] leading-[19.5px]"}`}
                    >
                      {totalItemPrice.toFixed(2)}
                    </span>
                  </div>

                  {/* Добавките — на собствен ред от chips */}
                  {addons.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {addons.map((a, idx) => (
                        <span
                          key={`a${idx}`}
                          className={`inline-flex items-center gap-1 bg-pos-addon-bg border border-pos-addon-brd text-pos-addon-ink rounded-[7px] px-1.5 py-0.5 ${isSheet ? "text-[11px]" : "text-[13px] leading-[16.5px]"}`}
                        >
                          <span className="text-pos-disc-ink">＋</span>
                          {a.name}
                          <span className="font-pos-mono">
                            {formatQuantity(a.totalQty)}×{a.price.toFixed(2)}
                          </span>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Модификаторите — на собствен ред, червени като коментара;
                    не се режат — пренасят се на нов ред при нужда */}
                  {modifiers.length > 0 && (
                    <div
                      className={`min-w-0 font-semibold italic text-pos-danger-ink break-words ${isSheet ? "text-[12.5px]" : "text-[14.5px] leading-[19px]"}`}
                    >
                      {modifiers.join(" · ")}
                    </div>
                  )}

                  {/* Коментарът към продукта — собствен ред, червен и четим
                    (същият стил като в детайла на масата) */}
                  {orderItem.comment && (
                    <div
                      className={`flex items-start gap-1.5 min-w-0 font-semibold italic text-pos-danger-ink ${isSheet ? "text-[13px]" : "text-[15px] leading-[19.5px]"}`}
                    >
                      <CommentOutlined className="text-xs shrink-0 mt-1" />
                      <span className="min-w-0 break-words">
                        {orderItem.comment}
                      </span>
                    </div>
                  )}

                  {/* Ред 3: количество × цена + малък степер */}
                  <div className="flex items-center gap-2">
                    <span
                      className={`font-pos-mono text-pos-muted flex-1 ${isSheet ? "text-[11px]" : "text-[13px] leading-[16.5px]"}`}
                    >
                      {formatQuantity(quantity)} × {price.toFixed(2)}
                    </span>
                    {stepper(row.indices, quantity)}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Футър: коментар към поръчката + Общо + бутони */}
      <div className="border-t border-pos-line2 px-4 py-3.5 flex flex-col gap-[11px]">
        {showCommentInput && (
          <div>
            <div className="font-pos-mono text-[11px] font-semibold uppercase tracking-[.06em] text-pos-dim mb-1.5">
              {t("ordering.orderComment")}
            </div>
            <TextArea
              value={comment}
              onChange={(e) => onCommentChange(e.target.value)}
              placeholder={t("ordering.commentPlaceholder")}
              // Enter тригерва „Поръчай" (shortcut ⏎) вместо нов ред;
              // Shift+Enter остава за нов ред.
              onPressEnter={(e) => {
                if (e.shiftKey) return;
                e.preventDefault();
                onSubmit();
              }}
              rows={2}
              autoFocus
              className="!bg-pos-panel !border-pos-ctl !rounded-xl !text-[13px]"
            />
          </div>
        )}

        {/* Клиент с отстъпка/надбавка по сметката: кой е избран + ±X%,
            отдолу основата и колко се добавя/маха. Показва се и при празна
            кошница — цените в каталога вече са коригирани. Клиент с 0% също
            се показва (закачен е към сметката). */}
        {billDiscount && (
          <div className="rounded-xl bg-pos-disc-bg px-3 py-2 flex flex-col gap-1">
            <div className="flex items-center justify-between gap-2">
              <span
                className={`min-w-0 flex items-center gap-1.5 font-semibold text-pos-ink ${isSheet ? "text-[12.5px]" : "text-[14.5px] leading-[19px]"}`}
              >
                <TeamOutlined className="shrink-0 text-[13px] text-pos-disc-ink" />
                <span className="min-w-0 truncate">
                  {billDiscount.clientName ?? t("ordering.billDiscountClient")}
                </span>
              </span>
              <span
                className={`shrink-0 font-pos-mono font-bold text-pos-disc-ink ${isSheet ? "text-[13px]" : "text-[15px] leading-[19.5px]"}`}
              >
                {formatSignedBillPercent(billDiscount.percent)}
              </span>
            </div>
            <div
              className={`flex items-center justify-between gap-2 font-pos-mono text-pos-soft ${isSheet ? "text-[11.5px]" : "text-[13.5px] leading-[17px]"}`}
            >
              <span>
                {t("ordering.billDiscountBase")} {pricing.baseTotal.toFixed(2)}
              </span>
              <span className="font-semibold text-pos-disc-ink">
                {t(
                  billDiscount.percent < 0
                    ? "ordering.billSurchargeLabel"
                    : "ordering.billDiscountLabel",
                )}{" "}
                {pricing.adjustment >= 0 ? "+" : "−"}
                {Math.abs(pricing.adjustment).toFixed(2)}
              </span>
            </div>
          </div>
        )}

        {/* Ред „Общо": вляво чекбоксът „Обобщено" (крие се при празна
            поръчка), вдясно етикетът и сумата. Без рамка и фон — само
            чекбокс + текст, оранжеви при включване. */}
        <div className="flex items-center justify-between gap-2">
          {!isEmpty ? (
            <button
              type="button"
              onClick={() => setSummarized((current) => !current)}
              aria-pressed={summarized}
              className={`inline-flex items-center gap-2 border-0 bg-transparent p-0 text-[12.5px] font-semibold cursor-pointer transition-colors ${
                summarized ? "text-pos-accent" : "text-pos-ink2"
              }`}
            >
              <span
                className={`grid h-4 w-4 flex-shrink-0 place-items-center rounded-[5px] border ${
                  summarized
                    ? "border-pos-accent bg-pos-accent text-white"
                    : "border-pos-check bg-pos-card"
                }`}
              >
                {summarized && <CheckOutlined className="text-[9px]" />}
              </span>
              {t("ordering.summarized")}
            </button>
          ) : (
            <span className="text-sm text-pos-soft2">{t("common.total")}</span>
          )}
          <span className="flex items-baseline gap-2 min-w-0">
            {!isEmpty && (
              <span className="text-sm text-pos-soft2">
                {t("common.total")}
              </span>
            )}
            <span className="font-pos-mono text-[22px] font-bold text-pos-ink whitespace-nowrap">
              {payableTotal.toFixed(2)} EUR
            </span>
          </span>
        </div>

        <div className="flex gap-2.5">
          <button
            type="button"
            onClick={onToggleComment}
            className={`shrink-0 h-11 px-4 rounded-xl border text-sm font-semibold inline-flex items-center gap-2 cursor-pointer ${
              showCommentInput
                ? "bg-pos-marked border-pos-accent text-pos-accent"
                : "bg-pos-card border-pos-ctl text-pos-ink2"
            }`}
          >
            <CommentOutlined className="text-pos-dim text-[15px]" />
            {t("common.comment")}
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={isEmpty || submitting}
            className="relative flex-1 h-11 rounded-xl bg-pos-accent hover:bg-pos-accent-ink text-white text-[15px] font-bold whitespace-nowrap cursor-pointer disabled:opacity-45 disabled:cursor-not-allowed transition-colors"
          >
            {t("ordering.order")}
            {/* Сумата в бутона: в sheet-а (телефон) винаги; в десктоп панела едва
                от xl (≥1280px) — под това панелът (27% от ширината) е твърде тесен и
                етикетът се пречупваше на два реда. */}
            <span className={isSheet ? "" : "hidden xl:inline"}>
              {" · "}
              {payableTotal.toFixed(2)}
            </span>
            {/* Kbd chip за Enter — само на десктоп панела (на телефон няма
                клавиатура). */}
            {!isSheet && <KbdChip shortcut="⏎" colorClass="text-white/80" />}
          </button>
        </div>
      </div>
    </div>
  );
};
